/**
 * favorites.ts — Helpers for persisting user model favorites.
 *
 * Logged-in users: stored in Firestore at users/{uid}/profile/favorites
 * Guests: stored in localStorage under "favorite_model_ids"
 */

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./config";

const LOCAL_KEY = "favorite_model_ids";

/** Load the user's favorite model IDs. */
export async function loadFavorites(uid: string | null): Promise<string[]> {
  if (uid) {
    try {
      const ref = doc(db, "users", uid, "profile", "favorites");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as { modelIds?: string[] };
        return Array.isArray(data.modelIds) ? data.modelIds : [];
      }
      return [];
    } catch {
      return [];
    }
  }
  // Guest — localStorage
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

/** Persist the user's favorite model IDs. */
export async function saveFavorites(
  uid: string | null,
  modelIds: string[],
): Promise<void> {
  if (uid) {
    const ref = doc(db, "users", uid, "profile", "favorites");
    await setDoc(
      ref,
      { modelIds, updatedAt: serverTimestamp() },
      { merge: true },
    );
    return;
  }
  // Guest — localStorage
  localStorage.setItem(LOCAL_KEY, JSON.stringify(modelIds));
}
